import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useBabyStore, useActiveBaby } from '@/stores/useBabyStore';
import { api } from '@/lib/api-client';
import type { JourneyResponse, JourneyCard as JourneyCardType, CardState } from '@/types/journey';
import { JourneyCard } from '@/components/journey/JourneyCard';
import { BabyAgeChip } from '@/components/profile/BabyAgeChip';
import { BottomNav } from '@/components/nav/BottomNav';
import { DailyContentCards } from '@/components/today/DailyContentCards';
import { PushPermissionPrompt } from '@/components/notifications/PushPermissionPrompt';
import { FullPageState } from '@/components/ui/PageState';
import { Icon, IconTile } from '@/components/ui/icons';
import { isGerman } from '@/i18n/locale';

function firstName(fullName: string | null | undefined): string | null {
  if (!fullName) return null;
  const trimmed = fullName.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

export default function Home() {
  const { babies, loaded, load } = useBabyStore();
  const baby = useActiveBaby();
  const [parentFirstName, setParentFirstName] = useState<string | null>(null);

  const [data, setData] = useState<JourneyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingCards, setLoadingCards] = useState(true);

  const de = isGerman();

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  useEffect(() => {
    // Fetch parent name for the greeting. Silent failure is fine — we just
    // fall back to a generic heading.
    api.profile
      .get()
      .then((res) => setParentFirstName(firstName(res.profile.full_name)))
      .catch(() => {
        /* ignore */
      });
  }, []);

  const refresh = useCallback(async (babyId: string) => {
    setLoadingCards(true);
    try {
      const res = await api.journey.thisWeek(babyId);
      setData(res);
      setError(null);
    } catch (err) {
      setError((err as Error).message ?? (de ? 'Diese Woche konnte nicht geladen werden.' : 'Failed to load this week.'));
    } finally {
      setLoadingCards(false);
    }
  }, [de]);

  useEffect(() => {
    if (baby) void refresh(baby.id);
  }, [baby, refresh]);

  const handleMark = useCallback(
    async (card: JourneyCardType, action: Exclude<CardState, 'unseen'>) => {
      if (!baby) return;

      // Optimistic local update — feels instant
      setData((prev) =>
        prev
          ? {
              ...prev,
              cards:
                action === 'dismissed'
                  ? prev.cards.filter((c) => c.id !== card.id)
                  : prev.cards.map((c) =>
                      c.id === card.id
                        ? {
                            ...c,
                            state: action,
                            state_updated_at: new Date().toISOString(),
                          }
                        : c
                    ),
              progress:
                action === 'dismissed'
                  ? { ...prev.progress, total: prev.progress.total - 1 }
                  : prev.progress,
            }
          : prev
      );

      try {
        await api.journey.log(card.id, baby.id, action);
      } catch {
        // Revert on failure — but quietly. The optimism is the right default.
        void refresh(baby.id);
      }
    },
    [baby, refresh]
  );

  if (!loaded) {
    return <FullPageState />;
  }
  if (babies.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!baby) {
    return <FullPageState />;
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-cream">
      <header className="sticky top-0 z-10 bg-cream/95 backdrop-blur border-b border-primary-50">
        <div className="mx-auto max-w-chat px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 rounded-lg" aria-label="Alma">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center" aria-hidden>
              <span className="text-white text-sm font-display font-semibold">A</span>
            </div>
            <span className="font-display text-lg font-semibold text-slate">Alma</span>
          </Link>
          <BabyAgeChip baby={baby} />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-chat px-4 py-5 space-y-4">
          <div>
            {parentFirstName && (
              <p className="text-sm text-slate-muted">
                {de ? `Hallo, ${parentFirstName}` : `Hi, ${parentFirstName}`}
              </p>
            )}
            <h1 className="font-display text-2xl font-semibold text-slate mt-0.5">
              {de ? `Diese Woche mit ${baby.name}` : `This week with ${baby.name}`}
            </h1>
            <p className="text-sm text-slate-muted mt-1">
              {de
                ? `Tipps, Meilensteine und sanfte Erinnerungen, abgestimmt auf das genaue Alter von ${baby.name}.`
                : `Tips, milestones, and gentle reminders tuned to ${baby.name}'s exact age.`}
            </p>
          </div>

          <PushPermissionPrompt babyName={baby.name} />

          <DailyContentCards baby={baby} />

          {error && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger-700">
              {error}
            </div>
          )}

          {loadingCards && !data && <CardSkeleton />}

          {data && data.cards.length === 0 && <EmptyWeek babyName={baby.name} />}

          {data?.cards.map((card) => (
            <JourneyCard
              key={card.id}
              card={card}
              onMark={(action) => handleMark(card, action)}
            />
          ))}

          {data && data.cards.length > 0 && (
            <p className="text-center text-xs text-slate-muted pt-2 pb-4">
              {de
                ? `Das war alles für etwa Woche ${data.baby.age_weeks}. Neue Karten erscheinen, sobald ${baby.name} weiterwächst.`
                : `That's everything for around week ${data.baby.age_weeks}. New cards appear as ${baby.name} grows.`}
            </p>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-5 animate-pulse">
          <div className="h-3 w-20 bg-primary-100 rounded" />
          <div className="h-5 w-3/4 bg-primary-100 rounded mt-3" />
          <div className="h-3 w-full bg-primary-50 rounded mt-3" />
          <div className="h-3 w-5/6 bg-primary-50 rounded mt-2" />
        </div>
      ))}
    </div>
  );
}

function EmptyWeek({ babyName }: { babyName: string }) {
  const de = isGerman();
  return (
    <div className="card p-8 text-center">
      <IconTile tone="primary" className="mx-auto">
        <Icon.Sprout size={20} />
      </IconTile>
      <h2 className="font-display font-semibold text-slate mt-3">
        {de ? 'Eine ruhige Woche' : "We're between scheduled cards"}
      </h2>
      <p className="text-sm text-slate-muted mt-2 max-w-xs mx-auto">
        {de
          ? `Diese Woche gibt es nichts Neues für ${babyName}s genaues Alter. Schau in ein paar Tagen wieder vorbei — oder frag Alma, was dich gerade beschäftigt.`
          : `Nothing new for ${babyName}'s exact age this week. Come back in a few days, or ask Alma anything that's on your mind.`}
      </p>
      <Link to="/chat" className="btn-secondary text-sm mt-4 py-2 px-4">
        {de ? 'Alma fragen' : 'Ask Alma'}
      </Link>
    </div>
  );
}
